import React from 'react';
import type { StagedAttachment } from '@/hooks/useAttachmentStaging';

interface StagedAttachmentsProps {
  items: StagedAttachment[];
  onRemove: (localId: string) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}

/**
 * The tray of files waiting above the composer — what turns "attach" from a send into a
 * decision the agent can still change their mind about.
 *
 * A failed entry stays visible with its reason rather than disappearing: silently dropping a
 * file the agent believes they attached is the worst outcome here, because they will tell the
 * customer "see attached" and never learn otherwise.
 */
export const StagedAttachments: React.FC<StagedAttachmentsProps> = ({ items, onRemove }) => {
  if (items.length === 0) return null;

  return (
    <div className="ac-staged-attachments" data-testid="staged-attachments">
      {items.map((item) => {
        const failed = item.status === 'failed';
        return (
          <div
            key={item.localId}
            className={'ac-staged-chip' + (failed ? ' failed' : '')}
            data-testid="staged-attachment"
            // An upload in flight is dimmed rather than blocked — remove must stay available,
            // since it is the one action an agent needs when they attached the wrong thing.
            style={{ opacity: item.status === 'uploading' ? 0.65 : 1 }}
          >
            {item.previewUrl ? (
              <img src={item.previewUrl} alt={item.fileName} className="ac-staged-thumb" />
            ) : (
              <span className="ac-staged-name" title={item.fileName}>
                {item.fileName}
                {!failed && <span className="ac-staged-size"> · {formatSize(item.sizeBytes)}</span>}
              </span>
            )}

            {failed && item.error && (
              // role=status so the reason is announced, and so tests have one stable hook for
              // "the agent was told why".
              <span role="status" className="ac-staged-error">
                {item.error}
              </span>
            )}

            <button
              type="button"
              className="ac-staged-remove"
              onClick={() => onRemove(item.localId)}
              aria-label={`Remove ${item.fileName}`}
            >
              &times;
            </button>
          </div>
        );
      })}
    </div>
  );
};
